import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MapCanvas } from '@/components/maps/MapCanvas';
import { LocationMarker } from '@/components/maps/LocationMarker';
import { createWaterSource, updateWaterSource, uploadWaterSourcePhoto } from '@/api/waterSources';
import { useAuthedObjectUrl } from '@/hooks/useAuthedObjectUrl';
import { compressImage } from '@/utils/compressImage';
import type { WaterSourceRecord } from '@/components/maps/WaterSourceDetailDrawer';

interface WaterSourceFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: number;
  /** When provided, the form is in edit mode. */
  source?: WaterSourceRecord | null;
  onSaved: (source: WaterSourceRecord) => void;
}

const SOUTH_AFRICA_CENTER: [number, number] = [-29.0, 26.0];

export function WaterSourceFormModal({
  open,
  onOpenChange,
  tenantId,
  source,
  onSaved,
}: WaterSourceFormModalProps) {
  const [name, setName] = useState('');
  const [sourceType, setSourceType] = useState<'HYDRANT' | 'PRIVATE'>('HYDRANT');
  const [condition, setCondition] = useState<string>('GOOD');
  const [position, setPosition] = useState<[number, number] | null>(null);
  const [address, setAddress] = useState('');
  const [capacityLitres, setCapacityLitres] = useState('');
  const [flowRateLpm, setFlowRateLpm] = useState('');
  const [accessNotes, setAccessNotes] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  // Only ever the object URL of a freshly picked file. The EXISTING photo
  // comes from the hook below — it sits behind the JWT authorizer, so the
  // old `getImageUrl()` src 401'd and the preview stayed blank when editing.
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const { url: existingPhotoUrl } = useAuthedObjectUrl('image', source?.photo_path ?? null, open);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed form when editing
  useEffect(() => {
    if (open && source) {
      setName(source.name ?? '');
      setSourceType(source.source_type ?? 'HYDRANT');
      setCondition(source.condition ?? 'GOOD');
      setPosition(source.lat != null && source.lng != null ? [source.lat, source.lng] : null);
      setAddress(source.address ?? '');
      setCapacityLitres(source.capacity_litres != null ? String(source.capacity_litres) : '');
      setFlowRateLpm(source.flow_rate_lpm != null ? String(source.flow_rate_lpm) : '');
      setAccessNotes(source.access_notes ?? '');
      setContactName(source.contact_name ?? '');
      setContactPhone(source.contact_phone ?? '');
      setContactEmail(source.contact_email ?? '');
      setNotes(source.notes ?? '');
    } else if (open && !source) {
      setName('');
      setSourceType('HYDRANT');
      setCondition('GOOD');
      setPosition(null);
      setAddress('');
      setCapacityLitres('');
      setFlowRateLpm('');
      setAccessNotes('');
      setContactName('');
      setContactPhone('');
      setContactEmail('');
      setNotes('');
    }
    setPhotoFile(null);
    setPhotoPreview(null);
    setError(null);
  }, [open, source]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!position) {
      setError('Please select a location on the map.');
      return;
    }
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Re-encode the photo to a compressed JPEG *before* saving anything.
      // This mirrors the wildfire photo flow (PhotoCapture → compressImage):
      // it converts HEIC (the default iPhone format, which the backend's PIL
      // validator rejects) into JPEG, normalises EXIF orientation, and shrinks
      // the upload. Doing it first means a decode failure surfaces an error
      // without leaving a half-saved record behind.
      let photoToUpload: File | null = null;
      if (photoFile) {
        try {
          photoToUpload = (await compressImage(photoFile)).file;
        } catch {
          setError(
            'Could not process that image. Please use a JPEG or PNG photo.',
          );
          setSaving(false);
          return;
        }
      }

      const payload = {
        tenant_id: tenantId,
        name: name.trim(),
        source_type: sourceType,
        condition,
        lat: position[0],
        lng: position[1],
        address: address.trim() || null,
        capacity_litres: capacityLitres ? parseInt(capacityLitres, 10) : null,
        flow_rate_lpm: flowRateLpm ? parseInt(flowRateLpm, 10) : null,
        access_notes: accessNotes.trim() || null,
        contact_name: contactName.trim() || null,
        contact_phone: contactPhone.trim() || null,
        contact_email: contactEmail.trim() || null,
        notes: notes.trim() || null,
      };
      let saved: WaterSourceRecord;
      if (source) {
        saved = await updateWaterSource(source.id, payload);
      } else {
        saved = await createWaterSource(payload);
      }
      if (photoToUpload) {
        saved = await uploadWaterSourcePhoto(saved.id, photoToUpload);
      }
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      // Surface the real backend error (e.g. an unsupported image format)
      // instead of swallowing it behind a generic message.
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err as Error)?.message ||
        'Failed to save. Please try again.';
      setError(detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{source ? 'Edit water source' : 'Add water source'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name + type */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="ws-name">Name *</Label>
              <Input
                id="ws-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Hydrant – Main & Oak"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Type *</Label>
              <Select value={sourceType} onValueChange={(v) => setSourceType(v as 'HYDRANT' | 'PRIVATE')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="HYDRANT">Hydrant (municipal)</SelectItem>
                  <SelectItem value="PRIVATE">Private (tank / dam / pool)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Condition *</Label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="GOOD">Good</SelectItem>
                  <SelectItem value="FAIR">Fair</SelectItem>
                  <SelectItem value="POOR">Poor</SelectItem>
                  <SelectItem value="OUT_OF_SERVICE">Out of service</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location picker */}
          <div className="space-y-1.5">
            <Label>Location * <span className="text-muted-foreground font-normal text-xs">(click map to place pin)</span></Label>
            <div className="h-52 overflow-hidden rounded-lg border border-border">
              <MapCanvas
                center={position ?? SOUTH_AFRICA_CENTER}
                zoom={position ? 14 : 6}
                gearMode="none"
                maximize={false}
                variant="inline"
                locationPicker={{
                  position,
                  onChange: setPosition,
                  enabled: true,
                }}
              />
            </div>
            {position && (
              <p className="text-xs text-muted-foreground">
                {position[0].toFixed(6)}, {position[1].toFixed(6)}
              </p>
            )}
          </div>

          {/* Address + specs */}
          <div className="space-y-1.5">
            <Label htmlFor="ws-address">Address / Description</Label>
            <Input id="ws-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. Corner Main Street & Oak Ave" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ws-capacity">Capacity (litres)</Label>
              <Input id="ws-capacity" type="number" min="0" value={capacityLitres} onChange={(e) => setCapacityLitres(e.target.value)} placeholder="e.g. 10000" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ws-flow">Flow rate (L/min)</Label>
              <Input id="ws-flow" type="number" min="0" value={flowRateLpm} onChange={(e) => setFlowRateLpm(e.target.value)} placeholder="e.g. 800" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ws-access">Access notes</Label>
            <Textarea id="ws-access" value={accessNotes} onChange={(e) => setAccessNotes(e.target.value)} className="h-20 resize-none" placeholder="Gate codes, distance from road, key holder info…" />
          </div>

          {/* Contact (shown always, relevant for private) */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Contact (optional)</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Input placeholder="Name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
              <Input placeholder="Phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
              <Input placeholder="Email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </div>
          </div>

          {/* Photo */}
          <div className="space-y-1.5">
            <Label>Photo <span className="text-muted-foreground font-normal text-xs">(optional, replaces existing)</span></Label>
            {(photoPreview ?? existingPhotoUrl) && (
              <img
                src={(photoPreview ?? existingPhotoUrl) as string}
                alt="Water source photo"
                className="h-36 w-full rounded-md object-cover border border-border"
              />
            )}
            <Input
              type="file"
              accept="image/jpeg,image/png,image/gif"
              className="cursor-pointer"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setPhotoFile(f);
                setPhotoPreview((prev) => {
                  if (prev) URL.revokeObjectURL(prev);
                  return f ? URL.createObjectURL(f) : null;
                });
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ws-notes">Notes</Label>
            <Textarea id="ws-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="h-20 resize-none" placeholder="Additional notes…" />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : source ? 'Save changes' : 'Add source'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
